export const OTC_STATUSES = [
  'any',
  'pending_confirmation',
  'new',
  'in_progress',
  'finalized',
  'confirmed',
  'settled',
  'cancelled',
];

export const OTC_STATUS_LABELS: Record<string, string> = {
  any: 'Any',
  pending_confirmation: 'Pend. Confirm.',
  new: 'New',
  in_progress: 'In Progress',
  finalized: 'Finalized',
  confirmed: 'Confirmed',
  settled: 'Settled',
  cancelled: 'Cancelled',
};

export function getOtcStatusLabel(status: string): string {
  return OTC_STATUS_LABELS[status] ?? status;
}

export function getOtcStatusColor(status: string): string {
  switch (status) {
    case 'finalized':
      return 'success';
    case 'settled':
      return 'success';
    case 'confirmed':
      return 'green';
    case 'cancelled':
      return 'error';
    case 'pending_confirmation':
      return 'warning';
    case 'in_progress':
      return 'orange';
    case 'new':
      return 'processing';
    default:
      return 'default';
  }
}

/**
 * Whether an OTC order's settlement calendar day has been reached
 * (`settlementDate` YYYY-MM-DD <= today). Approximates the desk check; the
 * backend is authoritative (Asia/Yerevan).
 */
export function isOtcSettlementDue(
  settlementDate: string | Date | null | undefined,
  today: Date = new Date()
): boolean {
  if (!settlementDate) return false;
  const settleDay = String(settlementDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(settleDay)) return false;
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return settleDay <= `${y}-${m}-${d}`;
}

/** Tooltip/toast text for a Settle action blocked by `isOtcSettlementDue`. */
export function getOtcSettlementPendingMessage(
  settlementDate: string | Date | null | undefined
): string {
  const day = String(settlementDate ?? '').slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return match
    ? `Settlement date (${match[3]}/${match[2]}/${match[1]}) has not been reached yet`
    : 'Settlement date has not been reached yet';
}

/**
 * Whether an instrument is fixed income. Tolerant of both fields because the
 * API populates them inconsistently — some rows carry
 * `assetClassId: 'fixed_income'`, others only a `descriptionAssetClass` of
 * `Bond`.
 */
export function isFixedIncome(
  instrument:
    | { assetClassId?: string | null; descriptionAssetClass?: string | null }
    | null
    | undefined
): boolean {
  const raw = `${instrument?.assetClassId ?? ''} ${
    instrument?.descriptionAssetClass ?? ''
  }`.toLowerCase();
  return raw.includes('fixed') || raw.includes('bond');
}

/**
 * Whether a fixed-income instrument has matured — its maturity date is on or
 * before today. Date comparison is calendar-day only (time-of-day ignored).
 */
export function isBondMatured(
  instrument:
    | { bondMaturityDate?: string | null; assetClassId?: string | null; descriptionAssetClass?: string | null }
    | null
    | undefined
): boolean {
  if (!instrument || !isFixedIncome(instrument)) return false;
  const maturity = instrument.bondMaturityDate;
  if (!maturity) return false;
  const maturityDay = String(maturity).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(maturityDay)) return false;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return maturityDay <= `${y}-${m}-${d}`;
}

/**
 * Presale allocations settle same-day (T+0): there is no secondary market to
 * settle into, so the usual T+1/T+2 conventions do not apply. Checked ahead of
 * `isFixedIncome` — a presale bond is still T+0.
 */
export function isPresaleInstrument(
  instrument: { isPresale?: boolean | null } | null | undefined
): boolean {
  return !!instrument?.isPresale;
}

/**
 * Minor-unit currencies, which have no balance of their own (GBX settles on
 * GBP at 100:1), so an OTC order can never be paid in them — the backend
 * rejects them as a payment currency. They can still be a price currency.
 * Mirrors SETTLEMENT_ALIASES in the backend.
 */
export const OTC_NON_PAYMENT_CURRENCIES = ['GBX'];

/**
 * The currencies an OTC order on this instrument may be paid in: the price
 * currency first, then — for an OTC instrument — every active fiat currency
 * except the minor units. Mirrors OtcPayment.allowedCurrencies on the backend.
 *
 * `currencies` is the currency list as the API returns it (CurrencyCacheService).
 */
export function otcPaymentCurrencies(
  instrument:
    | { priceCurrencyId?: string | null; isOtc?: boolean | null }
    | null
    | undefined,
  currencies: ReadonlyArray<{
    symbol?: string | null;
    type?: string | null;
    isActive?: boolean | null;
  }>
): string[] {
  const out: string[] = [];
  const price = instrument?.priceCurrencyId;
  if (price) out.push(price);
  if (!instrument?.isOtc) return out;

  for (const c of currencies) {
    const symbol = c?.symbol;
    if (
      symbol &&
      c.type === 'fiat' &&
      c.isActive !== false &&
      !OTC_NON_PAYMENT_CURRENCIES.includes(symbol) &&
      !out.includes(symbol)
    ) {
      out.push(symbol);
    }
  }
  return out;
}

/**
 * A suggested order rate — price currency per 1 payment currency — from the
 * desk's published FX rates, or null when there is no pair to read it from.
 *
 * Taken on the side that favours the desk, as the FX desk itself would quote:
 * on a buy the client hands over the payment currency, so the desk buys it; on
 * a sell the desk pays it out, so it sells it. The rate is only a starting
 * point — the operator can overwrite it.
 */
export function otcSuggestedPaymentRate(
  rates:
    | { base: string; quote: string; ask: number; bid: number }[]
    | null
    | undefined,
  priceCurrency: string,
  paymentCurrency: string,
  side: 'buy' | 'sell'
): number | null {
  if (!rates || !priceCurrency || !paymentCurrency) return null;
  if (priceCurrency === paymentCurrency) return null;

  const direct = rates.find(
    (r) => r.base === paymentCurrency && r.quote === priceCurrency
  );
  if (direct) {
    const rate = Number(side === 'buy' ? direct.bid : direct.ask);
    return rate > 0 ? rate : null;
  }

  const reverse = rates.find(
    (r) => r.base === priceCurrency && r.quote === paymentCurrency
  );
  if (reverse) {
    const rate = Number(side === 'buy' ? reverse.ask : reverse.bid);
    return rate > 0 ? Number((1 / rate).toFixed(6)) : null;
  }

  return null;
}

/**
 * One price-currency figure restated in the payment currency, for showing a price next to its
 * converted twin. Null when there is no usable rate.
 */
export function otcToPaymentCurrency(
  value: number | string | null | undefined,
  rate: number | null | undefined,
  precision = 2
): number | null {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  if (!rate || rate <= 0 || !Number.isFinite(amount)) return null;
  const factor = Math.pow(10, precision);
  return Math.round((amount / rate) * factor + 1e-9) / factor;
}

/**
 * A local preview of an OTC order's cash legs in the payment currency, for the
 * screens where the operator types the executed figures (finalize / confirm).
 * Mirrors OtcPayment.breakdown on the backend: each leg is converted on its
 * own and rounded half-up, and the total is summed from the rounded legs. The
 * backend recomputes all of it on submit; this only shows the operator what it
 * will be.
 */
export function otcPaymentPreview(
  side: 'buy' | 'sell',
  legs: { total: number; fee: number; aci?: number | null },
  rate: number | null | undefined,
  precision = 2
): { total: number; fee: number; aci: number; overallTotal: number } | null {
  if (!rate || rate <= 0) return null;
  const factor = Math.pow(10, precision);
  const convert = (value: number | null | undefined) => {
    const scaled = ((Number(value) || 0) / rate) * factor;
    return (Math.sign(scaled) * Math.round(Math.abs(scaled) + 1e-9)) / factor;
  };
  const total = convert(legs.total);
  const fee = convert(legs.fee);
  const aci = convert(legs.aci);
  const gross = total + aci;
  const overallTotal = side === 'buy' ? gross + fee : gross - fee;
  return {
    total,
    fee,
    aci,
    overallTotal: Math.round(overallTotal * factor) / factor,
  };
}
